import { Link } from "react-router-dom";
import { Gift, ArrowLeft, Sparkles, Star, ShoppingCart, ChevronLeft, Tag, TrendingUp, Flame, Truck, Shield, RotateCcw, Headphones, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { lazy, Suspense, useState, useMemo, useCallback, useRef } from "react";
import CrawlerContent from "@/components/CrawlerContent";
import SEOHead from "@/components/SEOHead";
import DailyMissionsBanner from "@/components/DailyMissionsBanner";
import { useAuth } from "@/hooks/useAuth";
import { usePoints } from "@/hooks/usePoints";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { productUrl } from "@/lib/productUrl";
import { optimizedImageUrl } from "@/lib/optimizedImageUrl";
import { useDynamicCategories } from "@/hooks/useDynamicCategories";
import { Skeleton } from "@/components/ui/skeleton";
import AnimatedSmileyPattern from "@/components/AnimatedSmileyPattern";
import smileyLogo from "@/assets/smileygift-logo.png";
import heroGiftPerson from "@/assets/hero-gift-person.png";
import catGiftBoxes from "@/assets/cat-gift-boxes.png";
import catHomeLiving from "@/assets/cat-home-living.png";
import catKidsBabies from "@/assets/cat-kids-babies.png";
import catBeauty from "@/assets/cat-beauty.png";
import catJewelry from "@/assets/cat-jewelry.png";
import catGaming from "@/assets/cat-gaming.png";
import catFoodChocolate from "@/assets/cat-food-chocolate.png";
import catArtCraft from "@/assets/cat-art-craft.png";

const ProductGrid = lazy(() => import("@/components/ProductGrid"));

const indexJsonLd = {
  "@type": "Store",
  "name": "SmileyGift",
  "description": "הפלטפורמה המובילה למתנות מותאמות אישית בישראל",
  "url": "https://www.smileyland.co.il",
  "currenciesAccepted": "ILS",
  "paymentAccepted": "Credit Card",
  "priceRange": "₪₪",
};

/* ── Mockup products removed — using real DB products ── */

/* ── Stats ── */
const sideFeatures = [
  { icon: Truck, label: "משלוח מהיר", val: "1-5 ימי עסקים", color: "text-primary", bg: "bg-primary/10", detail: "אנחנו שולחים את כל ההזמנות תוך 1-5 ימי עסקים ישירות עד הבית. המשלוחים מבוצעים באמצעות שליחים מקצועיים עם מעקב בזמן אמת." },
  { icon: Shield, label: "תשלום מאובטח", val: "הגנה מלאה", color: "text-success", bg: "bg-success/10", detail: "כל העסקאות מוצפנות בתקן SSL מתקדם ומאובטחות דרך חברת סליקה מורשית. פרטי האשראי שלכם לעולם לא נשמרים אצלנו." },
  { icon: RotateCcw, label: "החזרה קלה", val: "עד 30 יום", color: "text-accent", bg: "bg-accent/10", detail: "לא מרוצים? ניתן להחזיר את המוצר תוך 30 יום לכתובת החנות. לפרטים נוספים עיינו במדיניות ההחזרות." },
  { icon: Headphones, label: "תמיכה 24/7", val: "תמיד כאן", color: "text-hot", bg: "bg-hot/10", detail: "צוות התמיכה שלנו זמין 24 שעות ביממה, 7 ימים בשבוע. פנו אלינו בוואטסאפ, במייל או בטלפון." },
];

const featuredCategoryCards = [
  { image: catGiftBoxes, title: "מתנות", sub: "מתנות שאי אפשר לסרב להן", slug: "gifts" },
  { image: catHomeLiving, title: "בית וגן", sub: "פריטים שמשדרגים כל פינה", slug: "home-garden" },
  { image: catKidsBabies, title: "תינוקות", sub: "צעצועים ומתנות שמחות", slug: "babies" },
  { image: catBeauty, title: "יופי ובריאות", sub: "בשמים, קרמים ופינוקים", slug: "beauty-health" },
  { image: catJewelry, title: "שעונים", sub: "שעונים ותכשיטים מעוצבים", slug: "watches" },
  { image: catGaming, title: "משחקים", sub: "לגיימרים ולאוהבי הנאה", slug: "games" },
  { image: catFoodChocolate, title: "מטבח", sub: "כלי מטבח, שוקולד ופינוקים", slug: "kitchen" },
  { image: catArtCraft, title: "בהתאמה אישית", sub: "מתנות מותאמות אישית", slug: "custom" },
];

/* ── Real Product Card ── */

/* ── Real Product Card ── */
const FeaturedCard = ({ product }: { product: any }) => {
  const discount = product.original_price > product.price
    ? Math.round((1 - product.price / product.original_price) * 100)
    : 0;
  const imgSrc = optimizedImageUrl(product.image_url || "", 300, 65);

  return (
    <Link to={productUrl(product.name)} className="group flex-shrink-0 w-[160px] sm:w-[200px]">
      <div className="relative rounded-2xl overflow-hidden bg-card border border-primary/25 transition-all duration-300 hover:-translate-y-1.5 hover:border-primary/50"
        style={{ boxShadow: "0 0 12px hsl(var(--primary) / 0.08), 0 0 25px hsl(var(--primary) / 0.04)" }}
      >
        <div className="relative aspect-[4/5] overflow-hidden bg-muted/5">
          {imgSrc ? (
            <img src={imgSrc} alt={product.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-primary/5 text-3xl">🎁</div>
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-card/80 via-transparent to-transparent" />
          {discount > 0 && (
            <span className="absolute top-1.5 left-1.5 bg-destructive text-destructive-foreground text-[9px] font-black px-1.5 py-0.5 rounded-md z-10">
              -{discount}%
            </span>
          )}
        </div>
        <div className="p-3 border-t border-primary/10">
          <p className="text-sm font-bold line-clamp-2 leading-tight text-foreground mb-1.5">{product.name}</p>
          <div className="flex items-center justify-between">
            <span className="text-sm font-black text-primary">₪{product.price}</span>
            {discount > 0 && <span className="text-[10px] text-muted-foreground line-through">₪{product.original_price}</span>}
          </div>
        </div>
        <div className="absolute bottom-0 inset-x-0 h-[2px] bg-primary opacity-0 group-hover:opacity-100 transition-opacity" style={{ boxShadow: "0 0 8px hsl(var(--primary) / 0.6), 0 0 20px hsl(var(--primary) / 0.3)" }} />
      </div>
    </Link>
  );
};

/* ═══════════════════════════════════════════════════════
   MAIN INDEX PAGE
   ═══════════════════════════════════════════════════════ */
const Index = () => {
  const { user } = useAuth();
  const { balance } = usePoints();
  const { data: categories, isLoading: catLoading } = useDynamicCategories();
  const [heroFilter, setHeroFilter] = useState<string>("__top__");
  const [featurePopup, setFeaturePopup] = useState<number | null>(null);
  const heroRef = useRef<HTMLElement>(null);

  const { data: featuredProducts } = useQuery({
    queryKey: ["homepage-featured-products"],
    queryFn: async () => {
      const { data } = await supabase
        .from("products")
        .select("id, name, price, original_price, image_url, short_description, stock, rating, reviews_count, slug, badge, category")
        .eq("is_published", true)
        .gt("price", 0)
        .order("sold_count", { ascending: false })
        .limit(100);
      return data ?? [];
    },
    staleTime: 5 * 60 * 1000,
  });

  const topProducts = featuredProducts?.slice(0, 10) ?? [];
  const dealProducts = featuredProducts?.filter(p => p.original_price > p.price).slice(0, 10) ?? [];

  // Build category tabs from real data
  const heroCategoryTabs = useMemo(() => {
    if (!categories || categories.length === 0) return [];
    return categories.slice(0, 7).map(cat => ({
      key: cat.slug,
      label: cat.name,
      icon: cat.icon,
    }));
  }, [categories]);

  // Filter products for hero based on selected tab
  const heroProducts = useMemo(() => {
    if (!featuredProducts) return [];
    if (heroFilter === "__top__") return featuredProducts.slice(0, 8);
    if (heroFilter === "__deals__") return featuredProducts.filter(p => p.original_price > p.price).slice(0, 8);
    // Find the category group to get all subcategory names
    const group = categories?.find(c => c.slug === heroFilter);
    if (!group) return featuredProducts.slice(0, 8);
    const matchNames = new Set([group.name, ...group.subcategories.map(s => s.name)]);
    const filtered = featuredProducts.filter(p => matchNames.has(p.category));
    return filtered.length > 0 ? filtered.slice(0, 8) : featuredProducts.slice(0, 8);
  }, [featuredProducts, heroFilter, categories]);

  return (
    <div className="min-h-screen bg-background">
      <SEOHead keywords="מתנות מותאמות אישית, מתנות, smileyland" jsonLd={indexJsonLd} />

      {/* ═══════════ HERO — DASHBOARD WITH NAV + PRODUCTS ═══════════ */}
      <section ref={heroRef} className="relative overflow-hidden" data-hero>
        <style>{`[data-hero] { background: linear-gradient(160deg, hsl(var(--background)), hsl(var(--card)) 50%, hsl(var(--secondary) / 0.5)) !important; } html.dark [data-hero] { background: linear-gradient(160deg, hsl(45 40% 3%) 0%, hsl(42 30% 5%) 30%, hsl(38 25% 4%) 60%, hsl(50 35% 3%) 100%) !important; }`}</style>

        {/* Neon top border — MULTI-COLOR */}
        <div className="relative h-[3px] dark:h-[4px]">
          <div className="absolute inset-0 bg-gradient-to-r from-primary/0 via-primary to-primary/0" />
          <div className="absolute inset-0 bg-gradient-to-r from-primary/0 via-primary/60 to-primary/0 blur-[10px] dark:blur-[10px]" />
          <div className="hidden dark:block absolute inset-0 bg-gradient-to-r from-transparent via-primary/60 to-transparent blur-[25px]" />
          <div className="hidden dark:block absolute inset-0 bg-gradient-to-r from-transparent via-primary/30 to-transparent blur-[50px]" />
        </div>

        {/* Background glows — RICHER TONES */}
        <div className="absolute inset-0 pointer-events-none" style={{ animation: "color-shift 12s ease-in-out infinite" }}>
          <div className="absolute top-[-10%] right-[-5%] w-[700px] h-[700px] rounded-full" style={{ background: "radial-gradient(circle, hsl(45 100% 50% / 0.18), hsl(50 100% 55% / 0.06) 50%, transparent 75%)", animation: "glow-breathe 4s ease-in-out infinite" }} />
          <div className="absolute bottom-[-15%] left-[-8%] w-[600px] h-[600px] rounded-full" style={{ background: "radial-gradient(circle, hsl(40 100% 48% / 0.14), hsl(35 100% 45% / 0.04) 50%, transparent 70%)", animation: "glow-breathe 5s ease-in-out infinite 1s" }} />
          <div className="absolute top-[20%] left-[35%] w-[500px] h-[500px] rounded-full" style={{ background: "radial-gradient(circle, hsl(48 100% 55% / 0.12), transparent 65%)", animation: "glow-breathe 6s ease-in-out infinite 2s" }} />
          <div className="absolute top-[55%] right-[15%] w-[400px] h-[400px] rounded-full" style={{ background: "radial-gradient(circle, hsl(42 100% 60% / 0.1), transparent 60%)", animation: "float-glow 7s ease-in-out infinite" }} />
          {/* Extra warm gold glow center */}
          <div className="absolute top-[40%] left-[50%] -translate-x-1/2 w-[800px] h-[300px] rounded-full" style={{ background: "radial-gradient(ellipse, hsl(45 100% 52% / 0.06), transparent 70%)", animation: "glow-breathe 8s ease-in-out infinite 3s" }} />
        </div>

        {/* Neon particles — MORE & VARIED */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          {[...Array(30)].map((_, i) => {
            const hue = 38 + Math.random() * 20;
            const size = 1 + Math.random() * 3;
            return (
              <div key={i} className="absolute rounded-full"
                style={{
                  width: `${size}px`, height: `${size}px`,
                  background: `hsl(${hue} 100% ${55 + Math.random() * 25}%)`,
                  top: `${3 + Math.random() * 94}%`, left: `${1 + Math.random() * 98}%`,
                  opacity: 0.3 + Math.random() * 0.6,
                  animation: `neon-pulse ${1.5 + Math.random() * 5}s ease-in-out infinite ${Math.random() * 4}s`,
                  boxShadow: `0 0 ${4 + Math.random() * 12}px hsl(${hue} 100% 55% / 0.9), 0 0 ${15 + Math.random() * 25}px hsl(${hue} 100% 50% / 0.35)`,
                }}
              />
            );
          })}
        </div>

        {/* Neon streaks — diagonal light beams */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <div className="absolute top-[15%] left-0 w-[60%] h-[1px]" style={{ background: "linear-gradient(90deg, transparent, hsl(45 100% 55% / 0.25), transparent)", animation: "neon-streak 6s ease-in-out infinite", transformOrigin: "left" }} />
          <div className="absolute top-[45%] left-0 w-[40%] h-[1px]" style={{ background: "linear-gradient(90deg, transparent, hsl(48 100% 60% / 0.2), transparent)", animation: "neon-streak 8s ease-in-out infinite 2s", transformOrigin: "left" }} />
          <div className="absolute top-[70%] left-0 w-[50%] h-[1px]" style={{ background: "linear-gradient(90deg, transparent, hsl(42 100% 50% / 0.18), transparent)", animation: "neon-streak 7s ease-in-out infinite 4s", transformOrigin: "left" }} />
        </div>

        {/* Animated smiley pattern overlay */}
        <AnimatedSmileyPattern opacity={0.06} />

        {/* Horizontal neon accent lines — MULTI LAYER */}
        <div className="absolute top-[20%] left-0 right-0 h-px pointer-events-none" style={{ background: "linear-gradient(90deg, transparent 5%, hsl(45 100% 55% / 0.12) 30%, hsl(48 100% 60% / 0.08) 70%, transparent 95%)" }} />
        <div className="absolute top-[50%] left-0 right-0 h-px pointer-events-none" style={{ background: "linear-gradient(90deg, transparent 15%, hsl(42 100% 50% / 0.07) 50%, transparent 85%)" }} />
        <div className="absolute top-[80%] left-0 right-0 h-px pointer-events-none" style={{ background: "linear-gradient(90deg, transparent 10%, hsl(50 100% 55% / 0.09) 40%, hsl(45 100% 50% / 0.05) 60%, transparent 90%)" }} />

        <div className="container relative max-w-6xl mx-auto px-3 sm:px-4 py-4 sm:py-6">
          {/* Row 1: Brand + Quick Nav Buttons */}

          {/* Row 2: Category filter tabs */}
          <div className="flex gap-1.5 sm:gap-2 overflow-x-auto pb-2 mb-3 scrollbar-hide" style={{ scrollbarWidth: "none" }}>
            {/* Fixed tabs: Top sellers + Deals */}
            <button
              onClick={() => setHeroFilter("__top__")}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] sm:text-[11px] font-bold whitespace-nowrap transition-all hover:scale-[1.05] active:scale-95 flex-shrink-0"
              style={{
                background: heroFilter === "__top__" ? "hsl(var(--primary) / 0.15)" : "hsl(var(--muted))",
                border: `1px solid ${heroFilter === "__top__" ? "hsl(var(--primary) / 0.6)" : "hsl(var(--border))"}`,
                color: heroFilter === "__top__" ? "hsl(var(--primary))" : "hsl(var(--muted-foreground))",
                boxShadow: heroFilter === "__top__" ? "0 0 15px hsl(var(--primary) / 0.2)" : "none",
              }}
            >
              <Flame size={12} className="text-primary" />
              🔥 הנמכרים
            </button>
            <button
              onClick={() => setHeroFilter("__deals__")}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] sm:text-[11px] font-bold whitespace-nowrap transition-all hover:scale-[1.05] active:scale-95 flex-shrink-0"
              style={{
                background: heroFilter === "__deals__" ? "hsl(var(--primary) / 0.15)" : "hsl(var(--muted))",
                border: `1px solid ${heroFilter === "__deals__" ? "hsl(var(--primary) / 0.6)" : "hsl(var(--border))"}`,
                color: heroFilter === "__deals__" ? "hsl(var(--primary))" : "hsl(var(--muted-foreground))",
                boxShadow: heroFilter === "__deals__" ? "0 0 15px hsl(var(--primary) / 0.2)" : "none",
              }}
            >
              <Tag size={12} className="text-primary" />
              מבצעים
            </button>
            {/* Dynamic category tabs */}
            {heroCategoryTabs.map((tab) => {
              const isActive = heroFilter === tab.key;
              const TabIcon = tab.icon;
              return (
                <button
                  key={tab.key}
                  onClick={() => setHeroFilter(isActive ? "__top__" : tab.key)}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] sm:text-[11px] font-bold whitespace-nowrap transition-all hover:scale-[1.05] active:scale-95 flex-shrink-0"
                  style={{
                    background: isActive ? "hsl(var(--primary) / 0.15)" : "hsl(var(--muted))",
                    border: `1px solid ${isActive ? "hsl(var(--primary) / 0.6)" : "hsl(var(--border))"}`,
                    color: isActive ? "hsl(var(--primary))" : "hsl(var(--muted-foreground))",
                    boxShadow: isActive ? "0 0 15px hsl(var(--primary) / 0.2)" : "none",
                  }}
                >
                  <TabIcon size={12} className="text-primary" />
                  {tab.label}
                </button>
              );
            })}
          </div>

          {/* Row 3: Branded hero panel + side info */}
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_260px] xl:grid-cols-[1fr_300px] gap-3 mb-4">
            {/* Branded hero panel */}
            <div className="relative rounded-xl overflow-hidden group"
              style={{ border: "1px solid hsl(var(--primary) / 0.35)", boxShadow: "0 0 30px hsl(var(--primary) / 0.15), 0 0 60px hsl(var(--primary) / 0.06)" }}
            >
              <div
                className="absolute inset-0"
                style={{
                  background: "radial-gradient(circle at 18% 22%, hsl(var(--primary) / 0.28), transparent 24%), radial-gradient(circle at 82% 24%, hsl(var(--accent) / 0.2), transparent 20%), radial-gradient(circle at 50% 80%, hsl(var(--primary) / 0.16), transparent 28%), linear-gradient(135deg, hsl(var(--background)) 0%, hsl(var(--card)) 52%, hsl(var(--secondary) / 0.35) 100%)",
                }}
              />
              <div
                className="absolute inset-0 opacity-[0.08]"
                style={{
                  backgroundImage: `url("data:image/svg+xml,%3Csvg width='72' height='72' viewBox='0 0 72 72' xmlns='http://www.w3.org/2000/svg'%3E%3Ccircle cx='36' cy='36' r='18' fill='none' stroke='%23facc15' stroke-width='1.5'/%3E%3Ccircle cx='29' cy='32' r='2' fill='%23facc15'/%3E%3Ccircle cx='43' cy='32' r='2' fill='%23facc15'/%3E%3Cpath d='M27 42c3 4 15 4 18 0' stroke='%23facc15' stroke-width='1.5' stroke-linecap='round' fill='none'/%3E%3C/svg%3E")`,
                  backgroundSize: "72px 72px",
                }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-background/90 via-background/15 to-transparent" />
              {/* Neon corner accents */}
              <div className="absolute top-0 left-0 w-10 h-10 border-t-2 border-l-2 rounded-tl-xl" style={{ borderColor: "hsl(var(--primary) / 0.6)", boxShadow: "inset 3px 3px 10px hsl(var(--primary) / 0.2)" }} />
              <div className="absolute top-0 right-0 w-10 h-10 border-t-2 border-r-2 rounded-tr-xl" style={{ borderColor: "hsl(var(--primary) / 0.6)", boxShadow: "inset -3px 3px 10px hsl(var(--primary) / 0.2)" }} />
              <div className="absolute bottom-0 left-0 w-10 h-10 border-b-2 border-l-2 rounded-bl-xl" style={{ borderColor: "hsl(var(--primary) / 0.6)", boxShadow: "inset 3px -3px 10px hsl(var(--primary) / 0.2)" }} />
              <div className="absolute bottom-0 right-0 w-10 h-10 border-b-2 border-r-2 rounded-br-xl" style={{ borderColor: "hsl(var(--primary) / 0.6)", boxShadow: "inset -3px -3px 10px hsl(var(--primary) / 0.2)" }} />
              <div className="relative min-h-[180px] sm:min-h-[220px] lg:min-h-[280px] p-5 sm:p-6 lg:p-7 flex items-center">
                <div className="flex items-center justify-between gap-4 w-full">
                  {/* Text content - right side (RTL) */}
                  <div className="max-w-md text-right flex-1">
                    <div className="inline-flex items-center gap-2 rounded-full border border-primary/25 bg-background/50 px-3 py-1 text-[10px] font-bold text-primary backdrop-blur-sm mb-3">
                      <Sparkles size={12} />
                      ברוכים הבאים ל-SmileyGift
                    </div>
                    <h2 className="text-2xl sm:text-3xl lg:text-4xl font-black leading-tight text-foreground">
                      המתנות הכי שמחות,
                      <span className="block text-primary">במקום אחד!</span>
                    </h2>
                    <p className="mt-3 max-w-sm text-xs sm:text-sm leading-relaxed text-muted-foreground">
                      מגוון ענק של מתנות מיוחדות, משלוח מהיר וחוויית קנייה שמחה. הכל כאן, הכל בשבילכם 😊
                    </p>
                    <div className="mt-4 flex items-center gap-3 flex-wrap">
                      <Link to="/קטגוריות"
                        className="inline-flex items-center gap-1.5 bg-primary text-primary-foreground font-black px-5 py-2.5 rounded-full text-xs hover:scale-[1.04] active:scale-95 transition-all"
                        style={{ boxShadow: "0 0 20px hsl(var(--primary) / 0.7), 0 0 40px hsl(var(--primary) / 0.3)" }}
                      >
                        <Gift size={14} />
                        גלו את המתנות
                        <ArrowLeft size={12} />
                      </Link>
                      <span className="text-[10px] font-bold flex items-center gap-1 text-primary">
                        <Sparkles size={10} />
                        10% הנחה לנרשמים
                      </span>
                    </div>
                  </div>

                  {/* Hero image - left side (RTL), pinned to bottom border */}
                  <div className="hidden sm:block flex-shrink-0 relative self-end -mb-7">
                    <div className="absolute inset-0 -m-4 rounded-full blur-[40px] bg-primary/20" />
                    <img
                      src={heroGiftPerson}
                      alt="מתנות שמחות"
                      className="relative h-[240px] lg:h-[310px] w-auto object-contain drop-shadow-[0_0_30px_hsl(45_100%_50%/0.4)]"
                      width={768}
                      height={896}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Side: Quick stats + mini info */}
            <div className="hidden lg:flex flex-col gap-2.5 justify-center">
              {sideFeatures.map((s, i) => {
                const Icon = s.icon;
                return (
                  <div key={s.label} className={`flex items-center gap-3 p-3 rounded-xl border border-primary/25 bg-card/50 backdrop-blur-sm transition-all hover:scale-[1.02] hover:border-primary/40 cursor-pointer`}
                    style={{ boxShadow: "0 0 15px hsl(var(--primary) / 0.06)" }}
                    onClick={() => setFeaturePopup(i)}
                  >
                    <div className={`w-10 h-10 rounded-xl ${s.bg} flex items-center justify-center flex-shrink-0`}>
                      <Icon size={18} className={s.color} />
                    </div>
                    <div>
                      <p className="text-[11px] font-bold text-foreground">{s.label}</p>
                      <p className="text-[9px] text-muted-foreground">{s.val}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Row 4: Products inside hero — filtered by category tabs */}
          {heroProducts.length > 0 && (
          <div>
            <p className="text-xs font-black mb-2 flex items-center gap-1.5 text-primary" style={{ textShadow: "0 0 8px hsl(45 100% 50% / 0.3)" }}>
              <TrendingUp size={13} />
              {heroFilter === "__top__" ? "הנמכרים ביותר" : heroFilter === "__deals__" ? "מבצעים חמים" : categories?.find(c => c.slug === heroFilter)?.name || "מוצרים"}
            </p>
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 xl:grid-cols-8 gap-2.5 sm:gap-3">
              {heroProducts.map((p) => {
                const discount = p.original_price > p.price ? Math.round((1 - p.price / p.original_price) * 100) : 0;
                const imgSrc = optimizedImageUrl(p.image_url || "", 200, 65);
                return (
                  <Link key={p.id} to={productUrl(p.name)} className="group relative rounded-xl overflow-hidden transition-all duration-300 hover:-translate-y-1.5 bg-card border border-border hover:border-primary/40"
                    style={{
                      boxShadow: "0 0 15px hsl(var(--primary) / 0.06)",
                    }}
                  >
                    <div className="relative aspect-square overflow-hidden bg-muted/30">
                      {imgSrc ? (
                        <img src={imgSrc} alt={p.name} className="w-full h-full object-cover p-1 group-hover:scale-110 transition-transform duration-500" loading="lazy" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-2xl">🎁</div>
                      )}
                      {discount > 0 && (
                        <span className="absolute top-1.5 left-1.5 bg-destructive text-destructive-foreground text-[8px] font-black px-1.5 py-0.5 rounded shadow-sm">-{discount}%</span>
                      )}
                    </div>
                    <div className="p-2 sm:p-2.5 border-t border-border">
                    </div>
                    <div className="p-2 sm:p-2.5 border-t border-border">
                      <p className="text-[9px] sm:text-[10px] font-bold line-clamp-1 mb-1 text-foreground">{p.name}</p>
                      <div className="flex items-center justify-between">
                        <span className="text-[11px] sm:text-xs font-black text-primary">₪{p.price}</span>
                        {discount > 0 && <span className="text-[8px] sm:text-[9px] line-through text-muted-foreground">₪{p.original_price}</span>}
                      </div>
                    </div>
                    <div className="absolute bottom-0 inset-x-0 h-[2px] bg-primary/30 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </Link>
                );
              })}
            </div>
          </div>
          )}
        </div>

        {/* Bottom neon divider — MEGA GLOW */}
        <div className="relative h-[3px] dark:h-[5px]">
          <div className="absolute inset-0 bg-gradient-to-r from-primary/0 via-primary to-primary/0" />
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-primary/60 to-transparent blur-[8px]" />
          <div className="hidden dark:block absolute inset-0 bg-gradient-to-r from-transparent via-primary/60 to-transparent blur-[20px]" />
          <div className="hidden dark:block absolute inset-0 bg-gradient-to-r from-transparent via-primary/40 to-transparent blur-[40px]" />
          <div className="hidden dark:block absolute inset-0 bg-gradient-to-r from-transparent via-primary/20 to-transparent blur-[70px]" />
        </div>
      </section>

      {/* ═══════════ MAIN CONTENT ═══════════ */}
      <main className="container max-w-7xl mx-auto px-3 sm:px-4 space-y-6 sm:space-y-8 pb-8">

        {/* ── Stats Strip — NEON + Clickable ── */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {sideFeatures.map((s, i) => {
            const Icon = s.icon;
            return (
              <div key={s.label} className="flex items-center gap-3 p-3.5 rounded-xl bg-card border border-primary/25 hover:border-primary/50 transition-all hover:scale-[1.02] cursor-pointer"
                style={{ boxShadow: "0 0 15px hsl(var(--primary) / 0.08), 0 0 30px hsl(var(--primary) / 0.04)" }}
                onClick={() => setFeaturePopup(i)}
              >
                <div className={`w-11 h-11 rounded-xl ${s.bg} flex items-center justify-center flex-shrink-0`}>
                  <Icon size={20} className={s.color} />
                </div>
                <div>
                  <p className="text-xs font-bold text-primary">{s.label}</p>
                  <p className="text-[10px] text-muted-foreground">{s.val}</p>
                </div>
              </div>
            );
          })}
        </div>

        {/* ── Points Banner ── */}
        {user && (
          <Link to="/נקודות" className="block">
            <div className="flex items-center justify-between gap-3 bg-card border border-primary/25 rounded-2xl px-4 py-3 hover:border-primary/50 hover:shadow-[0_0_20px_hsl(var(--primary)/0.15)] transition-all"
              style={{ boxShadow: "0 0 10px hsl(var(--primary) / 0.08)" }}
            >
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-primary/20 flex items-center justify-center">
                  <Gift size={16} className="text-primary" />
                </div>
                <div>
                  <p className="text-sm font-bold text-foreground">יש לך <span className="text-primary font-black">{balance.toLocaleString()}</span> נקודות</p>
                  <p className="text-[10px] text-muted-foreground">💰 100 נקודות = ₪1 הנחה</p>
                </div>
              </div>
              <ArrowLeft size={14} className="text-primary" />
            </div>
          </Link>
        )}

        <DailyMissionsBanner />

        {/* ═══ קטגוריות סמיילי — Dynamic Category Cards ═══ */}
        <section>
          <div className="flex items-center justify-center mb-6">
            <div className="h-px flex-1 bg-gradient-to-l from-primary/40 to-transparent" />
            <h2 className="text-xl sm:text-2xl font-black text-primary px-6 flex items-center gap-2 dark:drop-shadow-[0_0_25px_hsl(var(--primary)/0.4)]"
            >
              ──── מה בא לך היום? 😊 ────
            </h2>
            <div className="h-px flex-1 bg-gradient-to-r from-primary/40 to-transparent" />
          </div>

           <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4" style={{ filter: "drop-shadow(0 0 2px hsl(var(--primary) / 0.15))" }}>
            {featuredCategoryCards.map((cat) => (
              <Link
                key={cat.slug}
                to={`/קטגוריה/${cat.slug}`}
                className="group relative overflow-hidden rounded-2xl border border-primary/30 bg-card transition-all duration-300 hover:-translate-y-1.5 hover:border-primary/60"
                style={{ boxShadow: "0 0 12px hsl(var(--primary) / 0.1), 0 0 30px hsl(var(--primary) / 0.05), inset 0 1px 0 hsl(var(--primary) / 0.08)" }}
              >
                <div className="relative aspect-square overflow-hidden bg-gradient-to-b from-primary/15 to-card">
                  <img
                    src={cat.image}
                    alt={cat.title}
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110 drop-shadow-[0_0_8px_hsl(var(--primary)/0.3)]"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-card via-card/10 to-transparent" />
                  <div className="absolute inset-0 opacity-0 transition-opacity duration-300 group-hover:opacity-100 bg-gradient-to-t from-primary/15 via-transparent to-primary/5" />
                  <div className="absolute top-0 left-0 w-6 h-6 border-t-2 border-l-2 border-primary/0 group-hover:border-primary/60 rounded-tl-2xl transition-all duration-300" />
                  <div className="absolute top-0 right-0 w-6 h-6 border-t-2 border-r-2 border-primary/0 group-hover:border-primary/60 rounded-tr-2xl transition-all duration-300" />
                </div>

                <div className="border-t border-primary/20 p-3 sm:p-4 relative">
                  <p className="mb-1 text-sm sm:text-base font-black leading-tight text-foreground">{cat.title}</p>
                  <p className="line-clamp-2 text-[10px] sm:text-[11px] leading-snug text-muted-foreground">{cat.sub}</p>
                </div>

                <div className="absolute inset-x-0 bottom-0 h-[2px] opacity-0 transition-opacity group-hover:opacity-100 bg-primary" style={{ boxShadow: "0 0 10px hsl(var(--primary) / 0.7), 0 0 25px hsl(var(--primary) / 0.4), 0 0 50px hsl(var(--primary) / 0.15)" }} />
              </Link>
            ))}
          </div>
        </section>

        {/* ═══ Categories Scrollbar ═══ */}
        <section className="rounded-2xl border border-primary/20 bg-card p-4 sm:p-5"
          style={{ boxShadow: "0 0 15px hsl(var(--primary) / 0.08), 0 0 40px hsl(var(--primary) / 0.04)" }}
        >
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-base sm:text-lg font-black text-primary flex items-center gap-2">
              <Sparkles size={16} />
              קטגוריות
            </h2>
            <Link to="/קטגוריות" className="text-[11px] font-bold text-primary hover:underline flex items-center gap-1">
              הכל <ChevronLeft size={12} />
            </Link>
          </div>
          <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-hide" style={{ scrollbarWidth: "none" }}>
            {catLoading
              ? Array.from({ length: 8 }).map((_, i) => (
                  <div key={i} className="flex flex-col items-center gap-2 p-3 min-w-[85px]">
                    <Skeleton className="w-14 h-14 rounded-xl" /><Skeleton className="h-3 w-12" />
                  </div>
                ))
              : (categories || []).slice(0, 12).map((cat) => (
                  <Link key={cat.name} to={`/קטגוריה/${cat.slug}`}
                    className="group flex flex-col items-center gap-2 p-3 rounded-2xl bg-card border border-primary/15 hover:border-primary/50 hover:shadow-[0_0_20px_hsl(var(--primary)/0.2),0_0_40px_hsl(var(--primary)/0.1)] transition-all hover:-translate-y-0.5 min-w-[85px]"
                    style={{ boxShadow: "0 0 8px hsl(var(--primary) / 0.06)" }}
                  >
                    <div className="w-14 h-14 rounded-xl overflow-hidden ring-1 ring-primary/25 group-hover:ring-primary/60 group-hover:shadow-[0_0_12px_hsl(var(--primary)/0.3)] transition-all">
                      {cat.circleImage && cat.circleImage !== "/placeholder.svg" ? (
                        <img src={cat.circleImage} alt={cat.name} className="w-full h-full object-cover" loading="lazy" />
                      ) : (
                        <div className="w-full h-full bg-gradient-to-br from-primary/30 to-accent/20 flex items-center justify-center">
                          <cat.icon size={24} className="text-primary" />
                        </div>
                      )}
                    </div>
                    <span className="text-[11px] font-bold text-foreground group-hover:text-primary transition-colors text-center leading-tight">
                      {cat.name}
                    </span>
                  </Link>
                ))
            }
          </div>
        </section>

        {/* ═══ מוצרים מהחמים — Real Products ═══ */}
        {topProducts.length > 0 && (
          <section className="relative">
            <div className="absolute -inset-4 pointer-events-none rounded-3xl" style={{ background: "radial-gradient(ellipse at 50% 0%, hsl(var(--primary) / 0.06), transparent 70%)" }} />
            <div className="flex items-center justify-center mb-5">
              <div className="h-px flex-1 bg-gradient-to-l from-primary/30 to-transparent" />
              <h2 className="text-lg sm:text-xl font-black text-primary px-5 flex items-center gap-2"
                style={{ textShadow: "0 0 20px hsl(var(--primary) / 0.3)" }}
              >
                <TrendingUp size={18} />
                מוצרים מפוצצים 💛
              </h2>
              <div className="h-px flex-1 bg-gradient-to-r from-primary/30 to-transparent" />
            </div>
            <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-hide" style={{ scrollbarWidth: "none" }}>
              {topProducts.map((p) => <FeaturedCard key={p.id} product={p} />)}
            </div>
          </section>
        )}

        {/* ═══ Deals Row ═══ */}
        {dealProducts.length > 0 && (
          <section className="rounded-2xl border border-primary/25 bg-card p-4 sm:p-5 relative overflow-hidden"
            style={{ boxShadow: "0 0 20px hsl(var(--primary) / 0.08), 0 0 50px hsl(var(--primary) / 0.04)" }}
          >
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[60%] h-[1px] bg-gradient-to-r from-transparent via-primary/40 to-transparent" style={{ boxShadow: "0 0 10px hsl(var(--primary) / 0.3), 0 0 30px hsl(var(--primary) / 0.15)" }} />
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-base sm:text-lg font-black text-foreground flex items-center gap-2">
                <Tag size={16} className="text-primary" />
                דילים שאסור לפספס 🏷️
              </h2>
              <Link to="/מבצעים" className="text-[11px] font-bold text-primary hover:underline flex items-center gap-1">
                הכל <ChevronLeft size={12} />
              </Link>
            </div>
            <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-hide" style={{ scrollbarWidth: "none" }}>
              {dealProducts.map((p) => <FeaturedCard key={p.id} product={p} />)}
            </div>
          </section>
        )}

        {/* ═══ Full Product Grid ═══ */}
        <Suspense fallback={<div className="min-h-[200px]" />}>
          <ProductGrid />
        </Suspense>

        {/* ═══ Vendor Recruitment Banner ═══ */}
        <section className="container max-w-5xl mx-auto px-4 py-4">
          <Link
            to="/הפוך-לספק"
            className="block relative overflow-hidden rounded-2xl border border-primary/25 group hover:border-primary/40 hover:shadow-lg hover:shadow-primary/10 transition-all duration-300"
            style={{ background: "linear-gradient(135deg, hsl(var(--primary) / 0.06), hsl(var(--accent) / 0.04), hsl(var(--primary) / 0.02))" }}
          >
            <div className="absolute inset-0 pointer-events-none">
              <div className="absolute -top-10 -right-10 w-32 h-32 bg-primary/10 rounded-full blur-[40px]" />
              <div className="absolute -bottom-10 -left-10 w-28 h-28 bg-accent/10 rounded-full blur-[30px]" />
            </div>
            <div className="relative p-5 md:p-7 flex items-center gap-4">
              <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center text-2xl flex-shrink-0">
                🏪
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-base md:text-lg font-black text-foreground">בעל עסק? יש לך מלאי מוצרים? <span className="text-primary"> הגיע הזמן למכור אונליין!</span></h3>
                <p className="text-sm text-muted-foreground mt-0.5">פתח חנות דיגיטלית ב-SmileyGift והתחל למכור לאלפי לקוחות. אנחנו מטפלים בכל השאר.</p>
              </div>
              <div className="hidden sm:flex items-center gap-1 text-primary font-bold text-sm group-hover:gap-2 transition-all">
                פתח חנות עכשיו
                <ChevronLeft size={16} className="group-hover:-translate-x-1 transition-transform" />
              </div>
            </div>
          </Link>
        </section>

        {/* ═══ SEO Block — Blue Neon ═══ */}
        <section className="container max-w-5xl mx-auto px-4 py-8">
          <div className="relative overflow-hidden rounded-2xl p-7 md:p-10 space-y-5 border border-blue-500/30 bg-gradient-to-br from-[#0a1628] via-[#0d1f3c] to-[#091428] shadow-xl shadow-blue-900/20">
            {/* Neon glow effects */}
            <div className="absolute top-0 left-1/4 w-72 h-72 bg-blue-500/20 rounded-full blur-[100px] pointer-events-none" />
            <div className="absolute bottom-0 right-1/4 w-64 h-64 bg-cyan-400/15 rounded-full blur-[80px] pointer-events-none" />
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-32 bg-blue-400/10 rounded-full blur-[60px] pointer-events-none" />
            
            {/* Scan line animation */}
            <div className="absolute inset-0 opacity-[0.03] pointer-events-none" style={{ backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(59,130,246,0.5) 2px, rgba(59,130,246,0.5) 3px)', backgroundSize: '100% 4px' }} />
            
            {/* Top accent line */}
            <div className="absolute top-0 left-[10%] right-[10%] h-[2px] bg-gradient-to-r from-transparent via-blue-400/70 to-transparent" />

            <h2 className="relative text-xl md:text-2xl font-black text-white tracking-wide drop-shadow-[0_0_10px_rgba(59,130,246,0.3)]">
              ✨ למה לקנות ב-SmileyGift?
            </h2>
            <p className="relative text-blue-50/90 leading-[1.9] text-sm font-medium">
              אנחנו מאמינים שכל מתנה צריכה להיות חוויה. לכן אצלנו תמצאו לא רק מוצרים, אלא גם אפשרות להתאמה אישית. הוסיפו טקסט, תמונה, או עיצוב ייחודי לכל מוצר מקטגוריית ההתאמה האישית. המחירים שלנו תחרותיים והקטלוג מתעדכן כל יום עם מוצרים חדשים ומבצעים בלעדיים.
            </p>
            <p className="relative text-blue-50/90 leading-[1.9] text-sm font-medium">
              התשלום מאובטח ב-100% עם כרטיס אשראי, ואנחנו מציעים מדיניות החזרה גמישה של 30 יום. צוות שירות הלקוחות שלנו זמין תמיד לעזור, בטלפון, באימייל ובצ׳אט. הצטרפו לאלפי הלקוחות המרוצים שכבר גילו את SmileyGift!
            </p>

            {/* Bottom accent line */}
            <div className="absolute bottom-0 left-[15%] right-[15%] h-[1px] bg-gradient-to-r from-transparent via-cyan-400/50 to-transparent" />
          </div>
        </section>
      </main>

      {/* Feature detail popup */}
      <AnimatePresence>
        {featurePopup !== null && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
            onClick={() => setFeaturePopup(null)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              transition={{ type: "spring", damping: 25, stiffness: 350 }}
              onClick={(e) => e.stopPropagation()}
              className="relative w-full max-w-sm bg-card border border-border/50 rounded-2xl p-6 shadow-2xl"
              dir="rtl"
            >
              <button
                onClick={() => setFeaturePopup(null)}
                className="absolute top-3 left-3 w-8 h-8 rounded-full bg-muted/80 flex items-center justify-center hover:bg-destructive hover:text-destructive-foreground transition-colors"
              >
                <X size={16} />
              </button>
              {(() => {
                const feat = sideFeatures[featurePopup];
                const Icon = feat.icon;
                return (
                  <div className="space-y-4">
                    <div className="flex items-center gap-3">
                      <div className={`w-12 h-12 rounded-xl ${feat.bg} flex items-center justify-center`}>
                        <Icon size={24} className={feat.color} />
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-foreground">{feat.label}</h3>
                        <p className="text-xs text-muted-foreground">{feat.val}</p>
                      </div>
                    </div>
                    <p className="text-sm text-muted-foreground leading-relaxed">{feat.detail}</p>
                  </div>
                );
              })()}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <CrawlerContent
        title="SmileyGift | מתנות שמביאות חיוך"
        description="SmileyGift היא הפלטפורמה המובילה למתנות מותאמות אישית בישראל."
        schemaType="https://schema.org/Store"
        sections={[
          { heading: "משלוח מהיר", body: "משלוח עד 3 ימי עסקים." },
          { heading: "תשלום מאובטח", body: "תשלום בכרטיס אשראי עם הגנה מלאה." },
        ]}
        links={[
          { label: "קטגוריות", href: "/קטגוריות" },
          { label: "מבצעים", href: "/מבצעים" },
        ]}
      />
    </div>
  );
};

export default Index;
